import React, { useState, useMemo, useEffect } from "react";
import PageLayout from "../../../Layouts/PageLayout/PageLayout";
import Table from "../../../components/common/Table/Table";
import { CgDanger } from "react-icons/cg";
import { CiCircleCheck } from "react-icons/ci";
import { getApiInstance } from "../../../utils/api";
import { mapList } from "../../../utils/utils";
import { useZone } from "../../../context/ZoneContext";
import { useTranslation } from "react-i18next";
import { FaClock } from "react-icons/fa";
import { CiClock1 } from "react-icons/ci";

const estados = [
    { label: "", type: "info" },
    { label: "Pendiente", type: "warning", Icon: FaClock },
    { label: "Incidencia", type: "danger", Icon: CgDanger },
    { label: "Validada", type: "success", Icon: CiCircleCheck },
];

export default function Productores({ user }) {
    const { t } = useTranslation();
    const [productores, setProductores] = useState();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [comunidades, setComunidades] = useState([]);
    const [paises, setPaises] = useState([]);
    const [provincias, setProvincias] = useState([]);
    const [selectedRows, setSelectedRows] = useState(new Set());
    const { selectedZone } = useZone();

    const columnasProductores = [
        { key: "id", label: "codProductor", visible: false },
        { key: "codProductor", label: "codProductor", visible: false },
        {
            key: "nombre",
            label: t("fields.name"),
            visible: true,
            sortable: true,
        },
        {
            key: "cif",
            label: t("fields.cif"),
            visible: true,
            sortable: true,
            visibleOnExpand: true,
        },
        {
            key: "contacto",
            label: t("fields.contact"),
            visible: true,
            sortable: true,
            visibleOnExpand: true,
            render: (row) => {
                return (
                    <div className="table-cell-content">
                        <span className="table-cell-content__primary">
                            {row.contacto || "-"}
                        </span>
                        <span className="table-cell-content__secondary">
                            {row.eMail || ""}
                        </span>
                    </div>
                );
            },
        },
        {
            key: "telefono",
            label: t("fields.phone"),
            visible: true,
            sortable: true,
            visibleOnExpand: true,
        },
        {
            key: "eMail",
            label: t("fields.email"),
            visible: false,
            sortable: false,
        },
        {
            key: "codEstado",
            label: t("fields.validation"),
            visible: true,
            sortable: true,
            visibleOnExpand: true,
        },
    ];

    useEffect(() => {
        fetchProductores();
        fetchComunidades();
        fetchPaises();
        fetchProvincias();
    }, [selectedZone]);

    const fetchProductores = async () => {
        setLoading(true);
        const api = getApiInstance();

        try {
            let productoresData = [];

            const response = await api.get(
                `/api/Productores/GetProductoresByZona/${selectedZone.value}`
            );
            productoresData = response.data;

            setProductores(productoresData);
            setError(null);
        } catch (err) {
            setError(
                "No se han podido cargar los productores. Vuelve a intentarlo más tarde."
            );
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const fetchComunidades = async () => {
        setLoading(true);
        const api = getApiInstance();

        try {
            let comunidadesData = [];

            const response = await api.get(
                `/api/Comunidades/GetComunidadesByZona/${selectedZone.value}`
            );
            comunidadesData = response.data;

            setComunidades(
                mapList(
                    comunidadesData,
                    "comunidad",
                    "codComunidad",
                    "codComunidad",
                    "codPais"
                )
            );

            setError(null);
        } catch (err) {
            setError(
                "No se han podido cargar las comunidades. Vuelve a intentarlo más tarde."
            );
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const fetchPaises = async () => {
        setLoading(true);
        const api = getApiInstance();

        try {
            let paisesData = [];

            const response = await api.get(
                `/api/Paises/GetPaisesByZona/${selectedZone.value}`
            );
            paisesData = response.data;

            setPaises(mapList(paisesData, "pais", "codPais", "codPais"));
            setError(null);
        } catch (err) {
            setError(
                "No se han podido cargar los países. Vuelve a intentarlo más tarde."
            );
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const fetchProvincias = async () => {
        setLoading(true);
        const api = getApiInstance();
        try {
            let provinciasData = [];

            const response = await api.get(
                `/api/Provincias/GetProvinciasByZona/${selectedZone.value}`
            );
            provinciasData = response.data;

            setProvincias(
                mapList(
                    provinciasData,
                    "provincia",
                    "codProvincia",
                    "codProvincia",
                    "codComunidad"
                )
            );
            setError(null);
        } catch (err) {
            setError(
                "No se han podido cargar las provincias. Vuelve a intentarlo más tarde."
            );
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const filterFields = [
        { key: "nombre", label: t("fields.name"), type: "multiselect" },
        { key: "cif", label: t("fields.cif"), type: "multiselect" },
        { key: "contacto", label: t("fields.contact"), type: "multiselect" },
        { key: "telefono", label: t("fields.phone"), type: "multiselect" },
        { key: "eMail", label: t("fields.email"), type: "multiselect" },
    ];

    const estados = [
        { type: "primary", label: t("states.noData") },
        { type: "info", label: t("states.pending"), Icon: CiClock1 },
        { type: "warning", label: t("states.incident"), Icon: CgDanger },
        { type: "success", label: t("states.validated"), Icon: CiCircleCheck },
    ];

    return (
        <PageLayout
            title={t("producers.title")}
            subtitle={t("producers.subtitle")}
        >
            <Table
                columns={columnasProductores}
                dataTable={productores}
                showCheckboxes={true}
                elementName={t("fields.producer")}
                noResultsMessage={t("producers.noProducersFound")}
                selectedRows={selectedRows}
                onSelectionChange={setSelectedRows}
                expandable={true}
                expandableBreakpoint={600}
                actions={["Eliminar", "Editar"]}
                searchTerms={[
                    t("fields.name"),
                    t("fields.cif"),
                    t("fields.phone"),
                    t("fields.email"),
                    t("fields.validation"),
                ]}
                formElements={[
                    {
                        title: t("sections.generalInfo"),
                        titleVisible: true,
                        elements: [
                            {
                                name: "nombre",
                                type: "text",
                                label: t("fields.name"),
                                placeholder: t("placeholders.enterName"),
                                required: true,
                                disabled: false,
                                labelVisible: true,
                                visible: true,
                            },
                            {
                                name: "cif",
                                type: "text",
                                label: t("fields.cif"),
                                placeholder: t("placeholders.enterCif"),
                                required: true,
                                disabled: false,
                                labelVisible: true,
                                visible: true,
                            },
                            {
                                name: "contacto",
                                type: "tel",
                                label: t("fields.contact"),
                                placeholder: t("placeholders.enterPhone"),
                                required: true,
                                disabled: false,
                                labelVisible: true,
                                visible: true,
                            },
                            {
                                name: "telefono",
                                type: "tel",
                                label: t("fields.phone"),
                                placeholder: t("placeholders.enterPhone"),
                                required: true,
                                disabled: false,
                                labelVisible: true,
                                visible: true,
                            },
                            {
                                name: "eMail",
                                type: "email",
                                label: t("fields.email"),
                                placeholder: t("placeholders.enterEmail"),
                                required: true,
                                disabled: false,
                                labelVisible: true,
                                visible: true,
                            },
                        ],
                    },
                    {
                        title: t("producers.form.representativeData"),
                        titleVisible: true,
                        elements: [
                            {
                                name: "nombreRepresentante",
                                type: "text",
                                label: t("producers.form.representativeName"),
                                placeholder: t(
                                    "producers.form.representativeNamePlaceholder"
                                ),
                                required: true,
                                disabled: false,
                                labelVisible: true,
                                visible: true,
                            },
                            {
                                name: "apellidosRepresentante",
                                type: "text",
                                label: t(
                                    "producers.form.representativeLastName"
                                ),
                                placeholder: t(
                                    "producers.form.representativeLastNamePlaceholder"
                                ),
                                required: true,
                                disabled: false,
                                labelVisible: true,
                                visible: true,
                            },
                            {
                                name: "dniRepresentante",
                                type: "text",
                                label: t("producers.form.representativeDni"),
                                placeholder: t(
                                    "producers.form.representativeDniPlaceholder"
                                ),
                                required: true,
                                disabled: false,
                                labelVisible: true,
                                visible: true,
                            },
                            {
                                name: "cargoRepresentante",
                                type: "text",
                                label: t(
                                    "producers.form.representativePosition"
                                ),
                                placeholder: t(
                                    "producers.form.representativePositionPlaceholder"
                                ),
                                required: true,
                                disabled: false,
                                labelVisible: true,
                                visible: true,
                            },
                            {
                                name: "telefonoContacto",
                                type: "tel",
                                label: t("producers.form.contactPhone"),
                                placeholder: t(
                                    "producers.form.contactPhonePlaceholder"
                                ),
                                required: true,
                                disabled: false,
                                labelVisible: true,
                                visible: true,
                            },
                            {
                                name: "eMailContacto",
                                type: "email",
                                label: t("producers.form.contactEmail"),
                                placeholder: t(
                                    "producers.form.contactEmailPlaceholder"
                                ),
                                required: true,
                                disabled: false,
                                labelVisible: true,
                                visible: true,
                            },
                            {
                                name: "cnae",
                                type: "number",
                                label: t("producers.form.cnae"),
                                placeholder: t(
                                    "producers.form.cnaePlaceholder"
                                ),
                                required: true,
                                disabled: false,
                                labelVisible: true,
                                visible: true,
                            },
                        ],
                    },
                    {
                        title: t("sections.location"),
                        titleVisible: true,
                        elements: [
                            {
                                name: "direccion",
                                type: "text",
                                label: t("fields.address"),
                                placeholder: t("placeholders.enterAddress"),
                                required: true,
                                disabled: false,
                                labelVisible: true,
                                visible: true,
                            },
                            {
                                name: "codPais",
                                type: "select",
                                label: t("fields.country"),
                                placeholder: t("placeholders.enterCountry"),
                                options: paises,
                                required: true,
                                disabled: false,
                                labelVisible: true,
                                visible: true,
                            },
                            {
                                name: "codComunidad",
                                type: "select",
                                label: t("fields.community"),
                                placeholder: t("placeholders.enterCommunity"),
                                options: comunidades,
                                required: true,
                                disabled: false,
                                labelVisible: true,
                                visible: true,
                                associatedFormElement: "codPais",
                            },
                            {
                                name: "codProvincia",
                                type: "select",
                                label: t("fields.province"),
                                placeholder: t("placeholders.enterProvince"),
                                options: provincias,
                                required: true,
                                disabled: false,
                                labelVisible: true,
                                visible: true,
                                associatedFormElement: "codComunidad",
                            },
                            {
                                name: "localidad",
                                type: "text",
                                label: t("fields.locality"),
                                placeholder: t("placeholders.enterLocality"),
                                required: true,
                                disabled: false,
                                labelVisible: true,
                                visible: true,
                            },
                            {
                                name: "cp",
                                type: "text",
                                label: t("fields.postalCode"),
                                placeholder: t("placeholders.enterPostalCode"),
                                required: true,
                                disabled: false,
                                labelVisible: true,
                                visible: true,
                            },
                            {
                                name: "codZona",
                                type: "number",
                                label: "Zona",
                                visible: false,
                                defaultValue: selectedZone.value,
                            },
                        ],
                    },
                ]}
                filterFields={filterFields}
                loading={loading}
                sortProp="nombre"
                addApiUrl="/api/Productores/SetProductor"
                editApiUrl="/api/Productores/UpdateProductor"
                deleteApiUrl="/api/Productores/DeleteProductor"
                keyProp="codProductor"
                stateProp="codEstado"
                states={estados}
                userCodAcceso={Number(user.codAcceso)}
                userCodPerfil={Number(user.role)}
            />
        </PageLayout>
    );
}
